import { Button } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core';
import {
    ColumnDef,
    ColumnFiltersState,
    FilterFn,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingFn,
    useReactTable
} from '@tanstack/react-table';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
    IOrderHistoryLine,
    IOrderHistorySchedulLine,
    IOrderHistoryTableData
} from '../../../actions/order-history/get-order-history.action';
import DataTitle from '../../../components/data-title';
import GenericFilterBox, { DateFilterConfig, FilterConfig, MultiFilterConfig } from '../../../components/filter-box';
import SearchBox from '../../../components/search-box';
import {
    ALL_STATUS,
    MULTI_VALUE_SEPARATOR,
    NO_DATA_FALLBACK,
    OPEN_STATUS,
    ORDER_HISTORY_COLUMN_ACTION,
    ORDER_HISTORY_COLUMN_ORDER_NUMBER,
    ORDER_HISTORY_COLUMN_PO_NUMBER,
    ORDER_HISTORY_COLUMN_SHIP_TO_NAME,
    ORDER_HISTORY_COLUMN_SOLD_TO_NAME,
    ORDER_HISTORY_DATE_FILTER_ONE_MONTH,
    ORDER_HISTORY_DATE_FILTER_THREE_MONTH,
    ORDER_HISTORY_DATE_FILTER_TWO_YEARS,
    ORDER_HISTORY_FILTER_CREATED_ON,
    ORDER_HISTORY_FILTER_DELIVERY_STATUS,
    ORDER_HISTORY_FILTER_EMPTY,
    ORDER_HISTORY_FILTER_FROM_DATE,
    ORDER_HISTORY_FILTER_FULLNAME,
    ORDER_HISTORY_FILTER_INVOICE_STATUS,
    ORDER_HISTORY_FILTER_LAST2YEARS,
    ORDER_HISTORY_FILTER_LAST3MONTH,
    ORDER_HISTORY_FILTER_LASTMONTH,
    ORDER_HISTORY_FILTER_RANGE,
    ORDER_HISTORY_FILTER_TO_DATE,
    ORDER_HISTORY_ORDER_TYPE,
    ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE,
    OrderStatus
} from '../../../configuration';
import {
    FILTER_BOX_CONFIRMATION_DATE_FILTER,
    FILTER_BOX_DELIVERY_STATUS_FILTER,
    FILTER_BOX_INVOICE_STATUS_FILTER
} from '../../../configuration/filter-box';
import { ILocalizationState } from '../../../states/localization-state';
import {
    dateLocaleFormatter,
    formatOverallStatusCodeForTooltip,
    formatStatusCodeForTooltip,
    mapItemInvoiceStatusCodeToText,
    mapItemStatusCodeToText,
    mapOverallDeliveryStatus,
    mapOverallDeliveryStatusCodeDescriptionToText,
    mapOverallInvoiceStatus,
    mapOverallInvoiceStatusCodeDescriptionToText,
    toLocalDate
} from '../../../utilities';
import { displayTableCellValue } from '../../../utilities/display-table-cell-value';
import { getOrderDetailsPageUrl } from '../../../utilities/get-order-details-page-url';
import { updateUrlParams, UrlParamAction } from '../../../utilities/update-url-params';
import { IOrderHistoryConfig, IOrderHistoryResources } from '../order-history.props.autogenerated';
import OrderHistoryTableResult from './order-history-table-result';

const getMatchingFilterString = (value: number) => {
    switch (value) {
        case 1:
            return ORDER_HISTORY_FILTER_LASTMONTH;
        case 3:
            return ORDER_HISTORY_FILTER_LAST3MONTH;
        case 24:
            return ORDER_HISTORY_FILTER_LAST2YEARS;
        default:
            return '';
    }
};

export default function OrderHistoryTable({
    config,
    resources,
    context,
    globalFilter,
    setGlobalFilter,
    orderHistoryTableData,
    orderHistoryReturnCodes,
    pageSize,
    handleOrderHistoryTableData,
    localizationState,
    excelConfig,
    isDataReady,
    CLASS_NAME
}: {
    config: IOrderHistoryConfig;
    resources: IOrderHistoryResources;
    context: ICoreContext;
    globalFilter: string;
    setGlobalFilter: Dispatch<SetStateAction<string>>;
    orderHistoryTableData: IOrderHistoryTableData[] | undefined;
    orderHistoryReturnCodes: number[];
    pageSize: number;
    props?: IOrderHistoryTableData;
    handleOrderHistoryTableData: (numMonths: Number, currentOrderView: string) => Promise<void>;
    localizationState: ILocalizationState;
    excelConfig: string[];
    isDataReady: boolean;
    CLASS_NAME: string;
}) {
    const url = new URL(window.location.href);
    const range = url.searchParams.get(ORDER_HISTORY_FILTER_RANGE) ?? '';
    const showSearchBoxFilters: boolean = true;

    const [expanded, setExpanded] = useState({});
    const [searchTerm, setSearchTerm] = useState<boolean>(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [data, setData] = useState<IOrderHistoryTableData[]>([]);
    const [activeButton, setActiveButton] = useState(range ? getMatchingFilterString(Number(range)) : '');
    const [clickedFilterButton, setClickedFilterButton] = useState(false);
    const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
        pageIndex: pageIndex,
        pageSize: pageSize
    });
    const [showFilterBox, setShowFilterBox] = useState<boolean>(true);
    const [currentView, setCurrentView] = useState<string>(OPEN_STATUS);
    const [currentConfig, setCurrentConfig] = useState(excelConfig);

    useEffect(() => {
        setCurrentConfig(excelConfig);
    }, [excelConfig]);

    useEffect(() => {
        if (orderHistoryTableData !== undefined) {
            setData(orderHistoryTableData);
        }
    }, [orderHistoryTableData, data]);

    useEffect(() => {
        const url = new URL(window.location.href);
        const fromDate = url.searchParams.get(ORDER_HISTORY_FILTER_FROM_DATE) ?? '';
        const toDate = url.searchParams.get(ORDER_HISTORY_FILTER_TO_DATE) ?? '';
        const filters = [];

        if (fromDate || toDate) {
            filters.push({ id: ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE, value: [fromDate, toDate] });
        }

        const deliveryStatus = url.searchParams.getAll(ORDER_HISTORY_FILTER_DELIVERY_STATUS);
        /**
         * Filter out invalid status codes, to prevent adding them to the filter state.
         */
        const validDeliveryStatus = deliveryStatus.filter(code => mapOverallDeliveryStatus(resources).getCodeExists(code));

        if (validDeliveryStatus.length > 0) {
            filters.push({
                id: ORDER_HISTORY_FILTER_DELIVERY_STATUS,
                value: validDeliveryStatus
                    .map(code => {
                        return mapOverallDeliveryStatus(resources).getText(code);
                    })
                    .join(MULTI_VALUE_SEPARATOR)
            });
        }

        const invoiceStatus = url.searchParams.getAll(ORDER_HISTORY_FILTER_INVOICE_STATUS);
        /**
         * Filter out invalid status codes, to prevent adding them to the filter state.
         */
        const validInvoiceStatus = invoiceStatus.filter(code => mapOverallInvoiceStatus(resources).getCodeExists(code));

        if (validInvoiceStatus.length > 0) {
            filters.push({
                id: ORDER_HISTORY_FILTER_INVOICE_STATUS,
                value: validInvoiceStatus
                    .map(code => {
                        return mapOverallInvoiceStatus(resources).getText(code);
                    })
                    .join(MULTI_VALUE_SEPARATOR)
            });
        }
        /**
         * If the URL contains invalid status codes, update the URL to remove them.
         */
        if (validDeliveryStatus.length !== deliveryStatus.length) {
            updateUrlParams({
                action: UrlParamAction.Set,
                paramName: ORDER_HISTORY_FILTER_DELIVERY_STATUS,
                value: validDeliveryStatus
            });
        }

        if (validInvoiceStatus.length !== invoiceStatus.length) {
            updateUrlParams({
                action: UrlParamAction.Set,
                paramName: ORDER_HISTORY_FILTER_INVOICE_STATUS,
                value: validInvoiceStatus
            });
        }

        setColumnFilters(filters);
    }, []);

    useEffect(() => {
        if (searchTerm && !globalFilter) {
            setSearchTerm(false);
            setExpanded({});
        }

        setPageIndex(0);
    }, [globalFilter, columnFilters]);

    const deliveryMultiSelectFilter: FilterFn<any> = (row, columnId, value) => {
        const currentValue = mapOverallDeliveryStatus(resources).getText(row.getValue(columnId));
        const selectedValues = value.split(MULTI_VALUE_SEPARATOR);
        return selectedValues.includes(currentValue);
    };

    const invoiceMultiSelectFilter: FilterFn<any> = (row, columnId, value) => {
        const currentValue = mapOverallInvoiceStatus(resources).getText(row.getValue(columnId));
        const selectedValues = value.split(MULTI_VALUE_SEPARATOR);
        return selectedValues.includes(currentValue);
    };

    /**
     * Filters a table row based on the nested structure of order lines and schedule lines,
     * checking if any schedule line's `ConfirmedDate` falls within the specified date range.
     *
     * @param row - Array of order lines.
     * @param value - Array with `from` and `to` date range.
     * @returns {boolean} - Returns `true` if any `ConfirmedDate` falls within the specified date range.
     */
    const nestedDateRangeFilter: FilterFn<any> = (row, _columnId, value) => {
        const [from, to] = value;
        const orderLines: IOrderHistoryLine[] = row.original.OrderLines || [];
        return orderLines.some((orderLine: IOrderHistoryLine) => {
            const scheduleLines = orderLine.ScheduleLines;
            return scheduleLines.some((scheduleLine: IOrderHistorySchedulLine) => {
                const currentValue = scheduleLine.ConfirmedDate;
                if (
                    currentValue &&
                    (!from || from === '' || new Date(from).getTime() <= new Date(currentValue).getTime()) &&
                    (!to || to === '' || new Date(currentValue).getTime() <= new Date(to).getTime())
                ) {
                    return true;
                } else {
                    return false;
                }
            });
        });
    };

    const dateRangeFilter: FilterFn<any> = (row, columnId, value) => {
        const currentValue = row.getValue<string>(columnId);
        const [from, to] = value;
        return (
            (!from || new Date(from).getTime() <= new Date(currentValue.split('T')[0]).getTime()) &&
            (!to || new Date(currentValue.split('T')[0]).getTime() <= new Date(to).getTime())
        );
    };

    /*
     * Sorting function to sort statuses of order items alphabetically.
     */
    const deliveryStatusSorting: SortingFn<IOrderHistoryTableData> = (rowA, rowB, columnId) => {
        return mapOverallDeliveryStatus(resources)
            .getText(rowA.getValue(columnId))
            .localeCompare(mapOverallDeliveryStatus(resources).getText(rowB.getValue(columnId)));
    };

    const invoiceStatusSorting: SortingFn<IOrderHistoryTableData> = (rowA, rowB, columnId) => {
        return mapOverallInvoiceStatus(resources)
            .getText(rowA.getValue(columnId))
            .localeCompare(mapOverallInvoiceStatus(resources).getText(rowB.getValue(columnId)));
    };

    const columns = useMemo<ColumnDef<IOrderHistoryTableData, any>[]>(
        () => [
            {
                accessorFn: row => row.OrderNumber,
                id: ORDER_HISTORY_COLUMN_ORDER_NUMBER,
                header: resources.orderHistoryOrderNumberText,
                cell: info => {
                    if (info.row.original.Status === OrderStatus.Queued) {
                        return flexRender(displayTableCellValue(info.getValue(), globalFilter), info);
                    }

                    const searchTerm = window.location.search.replace('?', '&');

                    return flexRender(
                        <a
                            href={`${getOrderDetailsPageUrl(context.actionContext, Number(info.row.original.OrderNumber))}${searchTerm}`}
                            onClick={e => e.stopPropagation()}
                            className={`${CLASS_NAME}__order-detail-link`}
                        >
                            {displayTableCellValue(info.getValue(), globalFilter)}
                        </a>,
                        info
                    );
                },
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.PoNumber,
                id: ORDER_HISTORY_COLUMN_PO_NUMBER,
                cell: info => displayTableCellValue(info.getValue(), globalFilter),
                header: () => resources.orderHistorySalesOrderPONumber,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.ConfirmedDate,
                id: ORDER_HISTORY_FILTER_CREATED_ON,
                header: resources.orderHistorySalesOrderCreatedOn,
                cell: info => {
                    const value = info.getValue();
                    return value ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(value)) : NO_DATA_FALLBACK;
                },
                footer: props => props.column.id,
                filterFn: dateRangeFilter
            },
            {
                accessorFn: row => row.StateLogistics,
                id: ORDER_HISTORY_FILTER_DELIVERY_STATUS,
                cell: info => {
                    const value = info.getValue();
                    return value ? (
                        <DataTitle
                            label={displayTableCellValue(mapOverallDeliveryStatus(resources).getText(String(value)), globalFilter)}
                            title={mapOverallDeliveryStatusCodeDescriptionToText(value, resources)}
                        />
                    ) : (
                        NO_DATA_FALLBACK
                    );
                },
                header: () => (
                    <span>
                        {resources.orderHistorySalesOrderDeliveryStatus}{' '}
                        <DataTitle
                            label={<span className='lxs-data-title__info-icon'></span>}
                            title={formatStatusCodeForTooltip(resources, context)}
                        />
                    </span>
                ),
                footer: props => props.column.id,
                filterFn: deliveryMultiSelectFilter,
                sortingFn: deliveryStatusSorting
            },
            {
                accessorFn: row => row.StateInvoice,
                id: ORDER_HISTORY_FILTER_INVOICE_STATUS,
                cell: info => {
                    const value = info.getValue();
                    return value ? (
                        <DataTitle
                            label={displayTableCellValue(mapOverallInvoiceStatus(resources).getText(String(value)), globalFilter)}
                            title={mapOverallInvoiceStatusCodeDescriptionToText(value, resources)}
                        />
                    ) : (
                        NO_DATA_FALLBACK
                    );
                },
                header: () => (
                    <span>
                        {resources.orderHistorySalesOrderInvoiceStatus}{' '}
                        <DataTitle
                            label={<span className='lxs-data-title__info-icon'></span>}
                            title={formatOverallStatusCodeForTooltip(resources, context)}
                        />
                    </span>
                ),
                footer: props => props.column.id,
                filterFn: invoiceMultiSelectFilter,
                sortingFn: invoiceStatusSorting
            },
            {
                accessorFn: row => row.SoldToName,
                id: ORDER_HISTORY_COLUMN_SOLD_TO_NAME,
                cell: info => displayTableCellValue(info.getValue(), globalFilter),
                header: () => resources.orderHistorySalesOrderSoldTo,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.ShipToName,
                id: ORDER_HISTORY_COLUMN_SHIP_TO_NAME,
                cell: info => displayTableCellValue(info.getValue(), globalFilter),
                header: () => resources.orderHistorySalesOrderShipTo,
                footer: props => props.column.id
            },

            {
                id: ORDER_HISTORY_COLUMN_ACTION,
                cell: info => info.getValue() ?? NO_DATA_FALLBACK,
                header: () => resources.orderHistorySalesOrderAction,
                footer: props => props.column.id,
                enableSorting: false
            },
            {
                id: ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE,
                header: () => resources.orderHistoryDetailsConfirmedDate,
                filterFn: nestedDateRangeFilter,
                enableHiding: true //utilize react-table filter functionality for nested column values while keeping original table header.
            }
        ],
        [globalFilter]
    );

    // Filter function to search for all order items based on user input
    const customGlobalFilter: FilterFn<IOrderHistoryTableData> = (row, _, filterValue) => {
        let highlightRow = false;

        filterValue !== '' && setSearchTerm(true);

        // Check for top-level properties
        const inMainData = Object.entries(row.original).some(([key, value]) => {
            if (typeof value === 'string') {
                if (key === 'StateLogistics') {
                    const mappedValue = mapOverallDeliveryStatus(resources).getText(value);
                    return mappedValue.toLowerCase().includes(filterValue.toLowerCase());
                } else if (key === 'StateInvoice') {
                    const mappedValue = mapOverallInvoiceStatus(resources).getText(value);
                    return mappedValue.toLowerCase().includes(filterValue.toLowerCase());
                } else {
                    return value.toLowerCase().includes(filterValue.toLowerCase());
                }
            }
            return false;
        });

        if (inMainData) {
            highlightRow = true;
        }

        // Check for OrderLines inside the top-level items
        const inOrderLines = row.original.OrderLines?.some(orderLine => {
            return Object.entries(orderLine)?.some(([key, value]) => {
                let mappedValue = value;

                if (typeof value === 'string') {
                    if (key === 'StateInvoice') {
                        mappedValue = mapItemInvoiceStatusCodeToText(value, resources);
                    } else if (key === 'StateLogistics') {
                        mappedValue = mapItemStatusCodeToText(value, resources);
                    }

                    const match = mappedValue
                        ?.toString()
                        ?.toLowerCase()
                        ?.includes(filterValue.toLowerCase());

                    if (match) {
                        highlightRow = true;
                    }
                    return match;
                }
            });
        });

        row.original.highlight = highlightRow;
        row.original.highlightOrderLines = inOrderLines;

        return highlightRow || inOrderLines;
    };

    const defaultSorting = useMemo(
        () => [
            {
                id: ORDER_HISTORY_FILTER_CREATED_ON,
                desc: true
            },
            {
                id: ORDER_HISTORY_COLUMN_ORDER_NUMBER,
                desc: true
            }
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        sortingFns: {},
        filterFns: {},
        initialState: { sorting: defaultSorting, columnVisibility: { confirmedDate: false } },
        state: {
            expanded,
            columnFilters,
            globalFilter,
            pagination: pagination
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: customGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onPaginationChange: setPagination,
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugTable: false,
        debugHeaders: false,
        debugColumns: false
    });

    useEffect(() => {
        const { columnFilters, sorting } = table.getState();
        const firstFilter = columnFilters[0]?.id;
        const firstSort = sorting[0]?.id;

        if (firstFilter === ORDER_HISTORY_FILTER_FULLNAME && firstSort !== ORDER_HISTORY_FILTER_FULLNAME) {
            table.setSorting([{ id: ORDER_HISTORY_FILTER_FULLNAME, desc: false }]);
        } else if (firstFilter !== ORDER_HISTORY_FILTER_FULLNAME && sorting.length === 0) {
            table.setSorting(defaultSorting);
        }
    }, [table.getState().columnFilters[0]?.id, table.getState().sorting]);

    const totalEntries = table.getFilteredRowModel().rows.length;

    const showFilter = config.showAllOrders;

    const getNumOfMonths = (period: string): { numMonths: Number } => {
        const numMonthsDefault = 0;
        let numMonths = 1;

        switch (period) {
            case ORDER_HISTORY_FILTER_LASTMONTH:
                numMonths = ORDER_HISTORY_DATE_FILTER_ONE_MONTH;
                break;
            case ORDER_HISTORY_FILTER_LAST3MONTH:
                numMonths = ORDER_HISTORY_DATE_FILTER_THREE_MONTH;
                break;
            case ORDER_HISTORY_FILTER_LAST2YEARS:
                numMonths = ORDER_HISTORY_DATE_FILTER_TWO_YEARS;
                break;
            default:
                numMonthsDefault;
        }
        return {
            numMonths: numMonths
        };
    };

    const handleNoOpenOrdersFound = async () => {
        setClickedFilterButton(true);
        setCurrentView(ALL_STATUS);
        setActiveButton(ORDER_HISTORY_FILTER_LASTMONTH);

        updateUrlParams({
            action: UrlParamAction.Set,
            paramName: ORDER_HISTORY_ORDER_TYPE,
            value: ALL_STATUS
        });
        updateUrlParams({
            action: UrlParamAction.Set,
            paramName: ORDER_HISTORY_FILTER_RANGE,
            value: ORDER_HISTORY_DATE_FILTER_ONE_MONTH.toString()
        });

        await handleOrderHistoryTableData(ORDER_HISTORY_DATE_FILTER_ONE_MONTH, ALL_STATUS).then(() => {
            setClickedFilterButton(false);
        });
    };

    const handleTimeFilterClick = async (buttonName: string) => {
        setPageIndex(0);
        setClickedFilterButton(true);
        setActiveButton(buttonName);
        setCurrentView(ALL_STATUS);
        const numMonths = getNumOfMonths(buttonName);

        updateUrlParams({
            action: UrlParamAction.Set,
            paramName: ORDER_HISTORY_FILTER_RANGE,
            value: numMonths.numMonths.toString()
        });

        updateUrlParams({
            action: UrlParamAction.Set,
            paramName: ORDER_HISTORY_ORDER_TYPE,
            value: ALL_STATUS
        });

        await handleOrderHistoryTableData(numMonths.numMonths, ALL_STATUS).then(() => {
            setClickedFilterButton(false);
        });
    };

    const handleOpenFilterClick = async () => {
        setCurrentView(OPEN_STATUS);
        setClickedFilterButton(true);
        const numMonths = ORDER_HISTORY_FILTER_EMPTY;
        setActiveButton(numMonths);

        updateUrlParams({
            action: UrlParamAction.Delete,
            paramName: ORDER_HISTORY_ORDER_TYPE
        });

        updateUrlParams({
            action: UrlParamAction.Delete,
            paramName: ORDER_HISTORY_FILTER_RANGE
        });

        await handleOrderHistoryTableData(Number(numMonths), OPEN_STATUS).then(() => {
            setClickedFilterButton(false);
        });
    };

    const handleConfigUpdate = (newConfig: string[]) => {
        setCurrentConfig(newConfig);
    };

    const filterConfig = useMemo<FilterConfig[]>(
        () => [
            {
                id: FILTER_BOX_CONFIRMATION_DATE_FILTER,
                type: 'date' as const,
                legend: resources.globalFilterConfirmedDateLegend,
                columnId: ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE,
                fromParamName: ORDER_HISTORY_FILTER_FROM_DATE,
                toParamName: ORDER_HISTORY_FILTER_TO_DATE
            } as DateFilterConfig,
            {
                id: FILTER_BOX_DELIVERY_STATUS_FILTER,
                type: 'multi' as const,
                legend: resources.orderHistoryFilterDeliveryStatusLegend,
                columnId: ORDER_HISTORY_FILTER_DELIVERY_STATUS,
                paramName: ORDER_HISTORY_FILTER_DELIVERY_STATUS,
                mapKeyToText: (key: string) => {
                    return mapOverallDeliveryStatus(resources).getText(key);
                }
            } as MultiFilterConfig,
            {
                id: FILTER_BOX_INVOICE_STATUS_FILTER,
                type: 'multi' as const,
                legend: resources.orderHistoryFilterInvoiceStatusLegend,
                columnId: ORDER_HISTORY_FILTER_INVOICE_STATUS,
                paramName: ORDER_HISTORY_FILTER_INVOICE_STATUS,
                mapKeyToText: (key: string) => {
                    return mapOverallInvoiceStatus(resources).getText(key);
                }
            } as MultiFilterConfig
        ],
        [resources]
    );

    useEffect(() => {
        const url = new URL(window.location.href);
        const orderType = url.searchParams.get(ORDER_HISTORY_ORDER_TYPE) ?? OPEN_STATUS;
        const dateRange = url.searchParams.get(ORDER_HISTORY_FILTER_RANGE) ?? ORDER_HISTORY_FILTER_EMPTY;

        setCurrentView(orderType);

        if (orderType === OPEN_STATUS) {
            setActiveButton(ORDER_HISTORY_FILTER_EMPTY);
        } else if (dateRange) {
            setActiveButton(getMatchingFilterString(Number(dateRange)));
        }
    }, []);

    return (
        <>
            <div className='row'>
                <div className={`col-12 ${CLASS_NAME}__container`}>
                    <>
                        <div className={`col ${showFilterBox ? `${CLASS_NAME}__search--small` : `${CLASS_NAME}__search--large`}`}>
                            <SearchBox
                                globalFilter={globalFilter ?? ''}
                                resources={resources}
                                currentView={currentView}
                                activeButton={activeButton}
                                showSearchBoxFilters={showSearchBoxFilters}
                                setGlobalFilter={setGlobalFilter}
                                setCurrentView={setCurrentView}
                                handleTimeFilterClick={handleTimeFilterClick}
                                handleOpenFilterClick={handleOpenFilterClick}
                            />
                        </div>
                        <div className={`col ${showFilterBox ? `${CLASS_NAME}__table--small` : `${CLASS_NAME}__table--large`}`}>
                            <OrderHistoryTableResult
                                orderHistoryReturnCodes={orderHistoryReturnCodes}
                                showFilter={showFilter}
                                isDataReady={isDataReady}
                                CLASS_NAME={CLASS_NAME}
                                totalEntries={totalEntries}
                                resources={resources}
                                activeButton={activeButton}
                                table={table}
                                globalFilter={globalFilter}
                                clickedFilterButton={clickedFilterButton}
                                localizationState={localizationState}
                                excelConfig={currentConfig}
                                context={context}
                                columnFilters={columnFilters}
                                currentView={currentView}
                                setExpanded={setExpanded}
                                setColumnFilters={setColumnFilters}
                                handleNoOpenOrdersFound={handleNoOpenOrdersFound}
                                onConfigUpdate={handleConfigUpdate}
                            />
                        </div>
                    </>
                    <div
                        className={`col-12 col-lg-3 order-1 order-lg-2 ${CLASS_NAME}__filter-box-wrapper ${
                            showFilterBox ? `${CLASS_NAME}__filter-box-wrapper--show` : `${CLASS_NAME}__filter-box-wrapper--hide`
                        }`}
                    >
                        <GenericFilterBox
                            table={table}
                            resources={resources}
                            context={context}
                            localizationState={localizationState}
                            columnFilters={columnFilters}
                            filters={filterConfig}
                            setColumnFilters={setColumnFilters}
                            isCollapsible={true}
                        />
                    </div>
                    <Button
                        title='show filter'
                        className={`${CLASS_NAME}__show-filter-btn ${
                            showFilterBox ? `${CLASS_NAME}__show-filter-btn--open` : `${CLASS_NAME}__show-filter-btn--closed`
                        }`}
                        onClick={() => setShowFilterBox(!showFilterBox)}
                    />
                </div>
            </div>
        </>
    );
}
